import { useRef, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';
import { useTracking } from 'react-tracking';

interface Response {
  onIntersection: (id: string) => void;
}

export const useImpressionTracking = (): Response => {
  const { trackEvent } = useTracking();

  const lastScrollY = useRef(0);
  const intersectedEventIds = useRef<string[]>([]);

  const trackScroll = useCallback(() => {
    trackEvent({
      type: 'track',
      subject: 'page',
      action: 'scroll_event',
      scrollY: lastScrollY.current,
      intersectedIds: intersectedEventIds.current,
    });
  }, [trackEvent]);

  const debounceTrackingEvent = debounce(() => {
    lastScrollY.current = window.scrollY;
    trackScroll();
  }, 1000);

  const handleScroll = useCallback(() => {
    if (window.scrollY > lastScrollY.current) {
      debounceTrackingEvent();
    }
  }, [lastScrollY, debounceTrackingEvent]);

  const handleIntersection = useCallback((id: string) => {
    if (!intersectedEventIds.current.includes(id)) {
      intersectedEventIds.current.push(id);
      debounceTrackingEvent();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    onIntersection: handleIntersection,
  };
};

export default useImpressionTracking;
